import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  Home, 
  BarChart3, 
  ShoppingCart, 
  Scale,
  Menu,
  X,
  ChevronLeft,
  Building,
  RefreshCw,
  Settings,
  TrendingUp,
  LogOut,
  User,
  UserCircle,
  Users,
  Building2
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Import pages
import HomePage from './pages/HomePage';
import ProjectsComparison from './pages/compare/ProjectsComparison';
import ProcurementApp from './pages/procurement';
import BalanceReview from './pages/balance/BalanceReview';
import BuildingsApp from './pages/buildings';
import SystemSettings from './pages/SystemSettings';
import AnalysisApp from './pages/analysis';
import { LoginPage, RegisterPage, MyProfilePage } from './pages/auth';
import AdminDashboard from './pages/admin/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const { isAuthenticated, loading, user, isSuperAdmin, isCompanyAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  // Check permission if required
  if (requiredPermission) {
    const hasPermission = () => {
      if (isSuperAdmin || isCompanyAdmin) return true;
      const permissions = user?.permissions || [];
      return permissions.includes('all') || permissions.includes(requiredPermission);
    };
    
    if (!hasPermission()) {
      return <Navigate to="/" replace />;
    }
  }
  
  return children;
};

// Sidebar Component
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();
  const { user, logout, isSuperAdmin, isCompanyAdmin } = useAuth();
  
  // Check if user has permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (isSuperAdmin || isCompanyAdmin) return true;
    const permissions = user.permissions || ['all'];
    return permissions.includes('all') || permissions.includes(permission);
  };
  
  const allMenuItems = [
    { path: '/', icon: Home, label: 'الرئيسية', color: 'text-blue-600', bgColor: 'bg-blue-50', permission: null },
    { 
      path: '/buildings', 
      icon: Building, 
      label: 'كميات العمائر',
      color: 'text-violet-600',
      bgColor: 'bg-violet-50',
      description: 'نظام إدارة كميات العمائر السكنية',
      permission: 'buildings'
    },
    { 
      path: '/analysis', 
      icon: TrendingUp, 
      label: 'تحليل الأسعار',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'تحليل فروقات الأسعار للأصناف',
      permission: 'analysis'
    },
    { 
      path: '/compare', 
      icon: BarChart3, 
      label: 'مقارنة المشاريع',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'مقارنة ملفات Excel',
      permission: 'compare'
    },
    { 
      path: '/procurement', 
      icon: ShoppingCart, 
      label: 'نظام المشتريات',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'عروض الأسعار والموردين',
      permission: 'procurement'
    },
    { 
      path: '/balance', 
      icon: Scale, 
      label: 'ميزان المراجعة',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      description: 'مقارنة موازين المراجعة',
      permission: 'balance'
    },
    { 
      path: '/system', 
      icon: Settings, 
      label: 'إعدادات النظام',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      description: 'النسخ الاحتياطي والصيانة',
      permission: 'system'
    },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => 
    item.permission === null || hasPermission(item.permission)
  );

  // Admin menu items
  const adminMenuItems = [];
  
  if (isSuperAdmin) {
    adminMenuItems.push(
      { 
        path: '/admin', 
        icon: Building2, 
        label: 'لوحة التحكم',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        description: 'إدارة الشركات والمستخدمين'
      }
    );
  }
  
  if (isCompanyAdmin) {
    adminMenuItems.push(
      { 
        path: '/admin/users', 
        icon: Users, 
        label: 'إدارة المستخدمين',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        description: 'إدارة موظفي الشركة'
      }
    );
  }

  const handleLogout = () => {
    logout();
    toast.success('تم تسجيل الخروج');
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      <aside 
        className={`
          fixed top-0 right-0 h-full bg-white shadow-2xl z-50 transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          w-[280px] lg:w-72
        `}
        dir="rtl"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-l from-blue-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-800">
                    نظام شامل للمشتريات
                  </h1>
                  <p className="text-[10px] sm:text-xs text-gray-500">
                    منصة موحدة متكاملة
                  </p>
                </div>
              </div>
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={22} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* User Info */}
          {user && (
            <div className="p-3 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                  {user.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.role === 'super_admin' && 'مدير النظام'}
                    {user.role === 'company_admin' && 'مدير الشركة'}
                    {user.role === 'employee' && 'موظف'}
                    {user.company_name && ` • ${user.company_name}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          <nav className="flex-1 p-3 overflow-y-auto">
            {/* Admin Menu */}
            {adminMenuItems.length > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-400 mb-2 px-3">الإدارة</p>
                <ul className="space-y-1.5 mb-4">
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path || 
                      (item.path !== '/' && location.pathname.startsWith(item.path));
                    
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                          className={`
                            flex items-center gap-3 p-3 rounded-xl transition-all duration-200
                            ${isActive 
                              ? `${item.bgColor} border-r-4 ${item.color.replace('text-', 'border-')} shadow-sm` 
                              : 'hover:bg-gray-50 active:bg-gray-100'
                            }
                          `}
                        >
                          <div className={`
                            w-9 h-9 rounded-lg flex items-center justify-center transition-colors
                            ${isActive ? item.bgColor : 'bg-gray-100'}
                          `}>
                            <Icon 
                              className={`w-5 h-5 ${isActive ? item.color : 'text-gray-400'} transition-colors`} 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-semibold truncate ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                              {item.label}
                            </div>
                            {item.description && (
                              <div className="text-[11px] text-gray-500 truncate">
                                {item.description}
                              </div>
                            )}
                          </div>
                          <ChevronLeft className={`w-4 h-4 ${isActive ? item.color : 'text-gray-300'}`} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <div className="border-t border-gray-100 my-2"></div>
              </>
            )}

            <p className="text-xs font-semibold text-gray-400 mb-2 px-3">القائمة الرئيسية</p>
            <ul className="space-y-1.5">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/' && location.pathname.startsWith(item.path));
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                      className={`
                        flex items-center gap-3 p-3 rounded-xl transition-all duration-200
                        ${isActive 
                          ? `${item.bgColor} border-r-4 ${item.color.replace('text-', 'border-')} shadow-sm` 
                          : 'hover:bg-gray-50 active:bg-gray-100'
                        }
                      `}
                    >
                      <div className={`
                        w-9 h-9 rounded-lg flex items-center justify-center transition-colors
                        ${isActive ? item.bgColor : 'bg-gray-100'}
                      `}>
                        <Icon 
                          className={`w-5 h-5 ${isActive ? item.color : 'text-gray-400'} transition-colors`} 
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                          {item.label}
                        </div>
                        {item.description && (
                          <div className="text-[11px] text-gray-500 truncate">
                            {item.description}
                          </div>
                        )}
                      </div>
                      <ChevronLeft className={`w-4 h-4 ${isActive ? item.color : 'text-gray-300'}`} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-3 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={() => {
                if ('caches' in window) {
                  caches.keys().then(names => {
                    names.forEach(name => caches.delete(name));
                  });
                }
                localStorage.clear();
                sessionStorage.clear();
                toast.success('تم مسح الذاكرة المؤقتة');
                setTimeout(() => {
                  window.location.reload(true);
                }, 500);
              }}
              className="w-full flex items-center justify-center gap-2 p-2.5 mb-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              تحديث الصفحة
            </button>
            <Link
              to="/profile"
              data-testid="my-profile-link"
              className="w-full flex items-center justify-center gap-2 p-2.5 mb-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-colors text-sm font-medium"
            >
              <UserCircle className="w-4 h-4" />
              حسابي
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              تسجيل الخروج
            </button>
            <div className="text-center mt-3 pt-3 border-t border-gray-200">
              <p className="text-[11px] text-gray-400">الإصدار 2.0.0 • © 2025</p>
              <p className="text-[11px] text-gray-500 font-medium mt-1">تطوير محمد مهدي</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className="lg:mr-72 min-h-screen">
        <header className="lg:hidden bg-white/95 backdrop-blur-md shadow-sm p-3 sticky top-0 z-30 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors"
            >
              <Menu size={22} className="text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-base font-bold text-gray-800">
                نظام شامل للمشتريات
              </h1>
            </div>
            <div className="w-10" />
          </div>
        </header>

        <main className="p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// App Routes
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />} />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout><HomePage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <MainLayout><MyProfilePage /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/buildings/*" element={
        <ProtectedRoute requiredPermission="buildings">
          <MainLayout><BuildingsApp /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/analysis/*" element={
        <ProtectedRoute requiredPermission="analysis">
          <MainLayout><AnalysisApp /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/compare" element={
        <ProtectedRoute requiredPermission="compare">
          <MainLayout><ProjectsComparison /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/procurement/*" element={
        <ProtectedRoute requiredPermission="procurement">
          <MainLayout><ProcurementApp /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/balance" element={
        <ProtectedRoute requiredPermission="balance">
          <MainLayout><BalanceReview /></MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/system" element={
        <ProtectedRoute requiredPermission="system">
          <MainLayout><SystemSettings /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
          <MainLayout><AdminDashboard /></MainLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
